import { ReactNode, useEffect, useState } from "react";
import { FederalHoliday, HolidayOption } from "types";
import {
  Autocomplete,
  useTheme,
  useMediaQuery,
  Theme,
  CircularProgress,
  Checkbox,
  AutocompleteRenderInputParams,
} from "@mui/material";
import { FEDERAL_HOLIDAYS, AUTURA_ASPHALT_LIGHT } from "utils";

import { TextValidator } from "react-material-ui-form-validator";
import { useTranslation } from "react-i18next";
import {
  ArrowDropDown,
  CheckBox,
  CheckBoxOutlineBlank,
} from "@mui/icons-material";

export interface HolidayAutocompleteProps {
  holidays: Array<FederalHoliday>;
  setHolidays: (holidays: Array<FederalHoliday>) => void;
  required?: boolean;
  readonly?: boolean;
  boolean?: boolean;
  margin?: "dense" | "normal" | "none";
  variant?: "standard" | "outlined" | "filled";
  size?: "small" | "medium";
  disabled?: boolean;
  loading?: boolean;
}

const HolidayAutocomplete = ({
  holidays,
  setHolidays,
  required = false,
  margin = "none",
  readonly = false,
  size,
  variant = "outlined",
  disabled = false,
  loading = false,
}: HolidayAutocompleteProps): ReactNode => {
  const { t } = useTranslation();
  const [holidayOptions, setHolidayOptions] = useState<HolidayOption[]>([]);

  useEffect(() => {
    const initial: HolidayOption[] = [];
    if (holidays) {
      holidays.forEach((holiday) => {
        const option = FEDERAL_HOLIDAYS.find((f) => f.label === holiday);
        if (option) {
          initial.push(option);
        }
      });
    }
    setHolidayOptions(initial);
  }, []);

  useEffect(() => {
    setHolidays(holidayOptions.map((h: HolidayOption) => h.label));
  }, [holidayOptions]);

  return (
    <Autocomplete
      multiple
      fullWidth
      size={size || "medium"}
      options={FEDERAL_HOLIDAYS}
      limitTags={5}
      getOptionLabel={(option) => option.label}
      disableCloseOnSelect
      value={holidayOptions}
      onChange={(event, newValue) => {
        setHolidayOptions(newValue);
      }}
      disabled={disabled}
      popupIcon={
        loading ? (
          <CircularProgress size={20} sx={{ color: AUTURA_ASPHALT_LIGHT }} />
        ) : (
          <ArrowDropDown />
        )
      }
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props}>
            <Checkbox
              icon={<CheckBoxOutlineBlank fontSize="small" />}
              checkedIcon={<CheckBox fontSize="small" />}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextValidator
          {...params}
          name="holiday"
          label={`${t("rule.data.holidayExceptions.label", {
            ns: "entities",
          })}${required ? " *" : ""}`}
          variant={variant}
          margin={margin}
          value={holidayOptions}
          validators={required ? ["required"] : []}
          errorMessages={[
            t("error.required", {
              name: t("rule.data.holidayExceptions"),
            }),
          ]}
          InputProps={{
            ...params.InputProps,
            readOnly: readonly,
          }}
          inputProps={{
            ...params.inputProps,
            style: {
              ...params.inputProps.style,
              cursor: readonly ? "default" : "text",
            },
          }}
        />
      )}
    />
  );
};

export default HolidayAutocomplete;
